import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 13: AI Platform Cloud Portability (Vendor Lock-in Remediation)
 * Strangler-fig migration of a GCP-locked AI product onto a self-hostable stack:
 * Firestore -> Postgres jsonb, Pinecone -> pgvector, Neo4j -> Apache AGE,
 * with config-gated backend seams and a control-plane / data-plane split.
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 6,
    fontWeight: 800,
    fontSize: 12,
    color: "#0f172a",
    width: 220,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0b2e59",
    width: 170,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#4a0b2a",
    width: 170,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0f3d1f",
    width: 190,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#92400e",
    width: 200,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#05333b",
    width: 170,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project13FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background groups
      {
        id: "bg-gcp",
        position: { x: 60, y: 40 },
        data: { label: "" },
        style: {
          width: 480,
          height: 620,
          background: "#fef2f240",
          border: "3px solid #fca5a5",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-seams",
        position: { x: 600, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 620,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-selfhost",
        position: { x: 1180, y: 40 },
        data: { label: "" },
        style: {
          width: 540,
          height: 620,
          background: "#ecfdf340",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-delivery",
        position: { x: 1780, y: 40 },
        data: { label: "" },
        style: {
          width: 500,
          height: 620,
          background: "#f5f3ff40",
          border: "3px solid #c4b5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Section titles
      { id: "sec-gcp", position: { x: 190, y: 50 }, data: { label: "Before: GCP-locked" }, style: nodeStyle.section },
      { id: "sec-seams", position: { x: 750, y: 50 }, data: { label: "Config-Gated Seams" }, style: nodeStyle.section },
      { id: "sec-selfhost", position: { x: 1340, y: 50 }, data: { label: "Self-Hosted Stack" }, style: nodeStyle.section },
      { id: "sec-delivery", position: { x: 1920, y: 50 }, data: { label: "Delivery & Verification" }, style: nodeStyle.section },

      // GCP column
      { id: "g1", position: { x: 200, y: 140 }, data: { label: "Cloud Run services" }, style: nodeStyle.compute },
      { id: "g2", position: { x: 200, y: 240 }, data: { label: "Firestore (documents)" }, style: nodeStyle.data },
      { id: "g3", position: { x: 200, y: 340 }, data: { label: "Pinecone (vectors)" }, style: nodeStyle.data },
      { id: "g4", position: { x: 200, y: 440 }, data: { label: "Neo4j (graph)" }, style: nodeStyle.data },
      { id: "g5", position: { x: 200, y: 540 }, data: { label: "Firebase Auth (kept as trust anchor)" }, style: { ...nodeStyle.net, width: 200 } },

      // Seams column
      { id: "m1", position: { x: 770, y: 140 }, data: { label: "Strangler-fig adapter pattern" }, style: { ...nodeStyle.note, width: 210 } },
      { id: "m2", position: { x: 770, y: 250 }, data: { label: "DB_BACKEND seam" }, style: nodeStyle.ops },
      { id: "m3", position: { x: 770, y: 340 }, data: { label: "VECTOR_BACKEND seam" }, style: nodeStyle.ops },
      { id: "m4", position: { x: 770, y: 430 }, data: { label: "GRAPH_BACKEND seam" }, style: nodeStyle.ops },
      { id: "m5", position: { x: 770, y: 530 }, data: { label: "Default = production byte-for-byte" }, style: { ...nodeStyle.note, width: 210 } },

      // Self-hosted column
      { id: "h1", position: { x: 1350, y: 140 }, data: { label: "Custom Postgres image (AGE + pgvector)" }, style: { ...nodeStyle.compute, width: 210 } },
      { id: "h2", position: { x: 1350, y: 250 }, data: { label: "Postgres jsonb (documents)" }, style: nodeStyle.data },
      { id: "h3", position: { x: 1350, y: 340 }, data: { label: "pgvector (vectors)" }, style: nodeStyle.data },
      { id: "h4", position: { x: 1350, y: 430 }, data: { label: "Apache AGE (graph)" }, style: nodeStyle.data },
      { id: "h5", position: { x: 1350, y: 530 }, data: { label: "docker-compose stack (healthchecks, env_file)" }, style: { ...nodeStyle.ops, width: 210 } },

      // Delivery column
      { id: "d1", position: { x: 1930, y: 140 }, data: { label: "SaaS control plane (Auth)" }, style: { ...nodeStyle.net, width: 200 } },
      { id: "d2", position: { x: 1930, y: 240 }, data: { label: "Customer-deployable data plane" }, style: { ...nodeStyle.net, width: 200 } },
      { id: "d3", position: { x: 1930, y: 340 }, data: { label: "Full test suite per change" }, style: nodeStyle.ops },
      { id: "d4", position: { x: 1930, y: 430 }, data: { label: "In-container e2e vs real Postgres" }, style: { ...nodeStyle.ops, width: 200 } },
      { id: "d5", position: { x: 1930, y: 530 }, data: { label: "Zero regressions" }, style: nodeStyle.note },
      { id: "d6", position: { x: 1930, y: 620 }, data: { label: "On-prem delivery unlocked" }, style: { ...nodeStyle.compute, width: 200 } },
    ],
    []
  );

  const edges = useMemo(() => {
    const red = { stroke: "#dc2626" };
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const purple = { stroke: "#7c3aed" };

    return [
      // GCP internal
      mkEdge("gg1", "g1", "g2", "reads/writes", { style: { ...red } }),
      mkEdge("gg2", "g1", "g3", "queries", { style: { ...red } }),
      mkEdge("gg3", "g1", "g4", "traverses", { style: { ...red } }),

      // GCP -> Seams
      mkEdge("gm1", "g2", "m2", "wrap", { style: { ...blue, strokeDasharray: "10 8" } }),
      mkEdge("gm2", "g3", "m3", "wrap", { style: { ...blue, strokeDasharray: "10 8" } }),
      mkEdge("gm3", "g4", "m4", "wrap", { style: { ...blue, strokeDasharray: "10 8" } }),

      // Seams internal
      mkEdge("mm1", "m1", "m2", "adapter", { style: { ...blue } }),
      mkEdge("mm2", "m1", "m3", "adapter", { style: { ...blue } }),
      mkEdge("mm3", "m1", "m4", "adapter", { style: { ...blue } }),
      mkEdge("mm4", "m1", "m5", "safe default", { style: { ...blue } }),

      // Seams -> Self-hosted (env flip)
      mkEdge("mh1", "m2", "h2", "env flip", { style: { ...green } }),
      mkEdge("mh2", "m3", "h3", "env flip", { style: { ...green } }),
      mkEdge("mh3", "m4", "h4", "env flip", { style: { ...green } }),

      // Self-hosted internal
      mkEdge("hh1", "h1", "h2", "hosts", { style: { ...green } }),
      mkEdge("hh2", "h1", "h3", "hosts", { style: { ...green } }),
      mkEdge("hh3", "h1", "h4", "hosts", { style: { ...green } }),
      mkEdge("hh4", "h1", "h5", "bundled", { style: { ...green } }),

      // Self-hosted -> Delivery
      mkEdge("hd1", "h5", "d2", "ships as", { style: { ...purple } }),
      mkEdge("hd2", "g5", "d1", "stays SaaS", { style: { ...purple, strokeDasharray: "10 8" } }),
      mkEdge("hd3", "h5", "d4", "verify", { style: { ...purple } }),

      // Delivery chain
      mkEdge("dd1", "d1", "d2", "auth handshake", { style: { ...purple } }),
      mkEdge("dd2", "d3", "d5", "gate", { style: { ...purple } }),
      mkEdge("dd3", "d4", "d5", "gate", { style: { ...purple } }),
      mkEdge("dd4", "d5", "d6", "unlock", { style: { ...purple } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "700px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.35}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
